import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { CalendarClock } from "lucide-react";
import { useApi, fmtD } from "@/lib/api";
import { brandHex } from "@/lib/brandColor";
import { Stat, Card, Pill } from "@/components/kit";

const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function Upcoming({ items }) {
  const now = Date.now();
  return (
    <Card>
      <h3 className="mb-4 flex items-center gap-2 text-lg font-bold"><CalendarClock className="h-4 w-4" />Renewals & trials · next 30 days</h3>
      <ul data-testid="revenue-upcoming" className="divide-y divide-slate-100">
        {items.map((u) => {
          const days = Math.ceil((new Date(u.date) - now) / 864e5);
          return (
            <li key={`${u.kind}-${u.business_id}`} data-testid={`upcoming-${u.business_id}`} className="flex flex-wrap items-center gap-2 py-2.5 text-sm">
              <span className="flex-1 font-semibold text-slate-900">{u.name}</span>
              <Pill status={u.kind === "trial" ? "due" : days <= 0 ? "overdue" : days <= 7 ? "exception" : "ok"} label={u.kind === "trial" ? "Trial ends" : `Manual · ${u.venues} venue(s)`} />
              <span className={`font-mono text-xs ${days <= 0 ? "text-rose-600" : "text-slate-500"}`}>{fmtD(u.date)} · {days <= 0 ? `${-days}d overdue` : `${days}d`}</span>
            </li>
          );
        })}
        {!items.length && <li className="py-4 text-sm text-slate-500">Nothing due in the next 30 days.</li>}
      </ul>
      <p className="mt-3 text-xs text-slate-400">Stripe subscriptions renew automatically and aren't listed.</p>
    </Card>
  );
}

export default function RevenueOverview() {
  const { data } = useApi("/platform/revenue");
  if (!data) return <div className="mb-8 h-32 animate-pulse rounded-2xl bg-slate-200/60" />;
  const chart = data.chart.map((c) => ({ ...c, label: MONTHS[Number(c.month.slice(5)) - 1] }));
  return (
    <div className="mb-10 space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Monthly income" value={gbp(data.monthly_income)} tone="emerald" sub="from active plans" testid="revenue-monthly-income" />
        <Stat label="Paying venues" value={data.paying_venues} sub={`${data.paying_businesses} business(es)`} testid="revenue-paying-venues" />
        <Stat label="Received this month" value={gbp(data.received_this_month)} sub={`Last month ${gbp(data.received_last_month)}`} testid="revenue-received-month" />
        <Stat label="Due in 30 days" value={data.upcoming.length} tone={data.upcoming.length ? "amber" : "slate"} sub="renewals & trial ends" testid="revenue-upcoming-count" />
      </div>
      <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <Card>
          <h3 className="mb-4 text-lg font-bold">Payments received · 6 months</h3>
          <div data-testid="revenue-chart" className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}><XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} /><YAxis tickLine={false} axisLine={false} fontSize={12} width={48} tickFormatter={(v) => `£${v}`} /><Tooltip formatter={(v) => gbp(v)} cursor={{ fill: "#F1F5F9" }} /><Bar dataKey="amount" fill={brandHex()} radius={[6, 6, 0, 0]} name="Received" /></BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Upcoming items={data.upcoming} />
      </div>
    </div>
  );
}
